import { useMutation } from '@tanstack/react-query';
import { axiosClient } from '../axiosClient';
import { SalesRefResponseType } from '../../types/apiTypes';
import { CoordinatesType, TableIdsType } from '../../types/dataTypes';
import { headers } from '../queryClient';
import { getTableIdsAsArray, getPolygonApiData } from '@/api/apiHelpers';
import * as Sentry from '@sentry/react';
import { getRT90fromWGS84, calculateBounds } from '@/helpers';

interface props {
  coordinates: CoordinatesType | null,
  parameters: any,
  soldTableIds: TableIdsType,
  polygonCoordinates: number[][] | null,
  distance: number
}

const useSalesRef = () => {
  return useMutation({
    mutationFn: async ({
      coordinates,
      parameters,
      soldTableIds,
      polygonCoordinates,
      distance
    }: props): Promise<SalesRefResponseType | null> => {
      if(!coordinates){
        return null;
      }
      const { lat, lng } = coordinates;

      const excludedIds = getTableIdsAsArray(soldTableIds.excluded);
      const selectedIds = getTableIdsAsArray(soldTableIds.selected);
      const polygons = getPolygonApiData(polygonCoordinates);

      const bounds = calculateBounds(coordinates, distance);
      const { east, north, south, west } = bounds;

      const cMax = { lat: north, lng: east };
      const cMin = { lat: south, lng: west };
      const transformedCoordsMax = getRT90fromWGS84(cMax.lat, cMax.lng);
      const transformedCoordsMin = getRT90fromWGS84(cMin.lat, cMin.lng);
      const body = {
        ...parameters,
        selected_ids: selectedIds,
        excluded_ids: excludedIds,
        wgs84lat: lat.toString(),
        wgs84lon: lng.toString(),
        rt90x_square_min: transformedCoordsMin.rt90x,
        rt90y_square_min: transformedCoordsMin.rt90y,
        rt90x_square_max: transformedCoordsMax.rt90x,
        rt90y_square_max: transformedCoordsMax.rt90y,
        polygons,
      };

      const URL = `${import.meta.env.VITE_API_SALES_REF_URL}`;

      const { data } = await axiosClient.post<SalesRefResponseType>(
        URL,
        body,
        headers
      );
      return data;
    },
    onError: (error) => {
      Sentry.captureException(error);
    },
    onSuccess: (data) => {
      return data;
    }
  });
};

export default useSalesRef;
